import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { Page, PageMetadata, SortOrder } from '@tran-monorepo/interfaces';
import { AwardCommitteeMeeting } from '@tran-monorepo/interfaces/applications';
import { EMPTY, Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { AwardCommitteeMeetingsService } from '../../services/award-committee-meetings.service';
import { LoadMeetings, LoadMoreMeetings } from './award-committee-meetings.actions';

export interface AwardCommitteeMeetingsStateModel {
    loading: boolean;
    meetings: AwardCommitteeMeeting[];
    metadata: PageMetadata | null;
    pageSize: number;
}

@State<AwardCommitteeMeetingsStateModel>({
    name: 'awardCommitteeMeetings',
    defaults: {
        loading: false,
        meetings: [],
        metadata: null,
        pageSize: 20
    }
})
@Injectable()
export class AwardCommitteeMeetingsState {
    private readonly awardCommitteeMeetingsService = inject(AwardCommitteeMeetingsService);

    @Selector()
    static meetings(state: AwardCommitteeMeetingsStateModel): AwardCommitteeMeeting[] {
        return state.meetings;
    }

    @Selector()
    static loading(state: AwardCommitteeMeetingsStateModel): boolean {
        return state.loading;
    }

    @Selector()
    static metadata(state: AwardCommitteeMeetingsStateModel): PageMetadata | null {
        return state.metadata;
    }

    @Action(LoadMeetings)
    load(
        ctx: StateContext<AwardCommitteeMeetingsStateModel>,
        { payload: { pageSize } }: LoadMeetings
    ): Observable<Page<AwardCommitteeMeeting>> {
        ctx.patchState({ loading: true, pageSize });

        const startDate = new Date();
        startDate.setUTCHours(0, 0, 0, 0);

        return this.awardCommitteeMeetingsService
            .getMeetings({ page: 0, pageSize, orderBy: 'startDate', sortOrder: SortOrder.ASC }, startDate.toISOString())
            .pipe(
                tap(({ items: meetings, metadata }) =>
                    ctx.patchState({
                        meetings: meetings,
                        metadata
                    })
                ),
                catchError(() => {
                    ctx.patchState({ meetings: [], metadata: null });
                    return EMPTY;
                }),
                finalize(() => ctx.patchState({ loading: false }))
            );
    }

    @Action(LoadMoreMeetings)
    loadMore(ctx: StateContext<AwardCommitteeMeetingsStateModel>): Observable<Page<AwardCommitteeMeeting>> | void {
        const { loading, metadata, pageSize } = ctx.getState();

        if (loading || !metadata?.hasNextPage) {
            return;
        }

        const page = (metadata?.currentPage ?? 0) + 1;
        ctx.patchState({ loading: true });

        return this.awardCommitteeMeetingsService
            .getMeetings({ page, pageSize, orderBy: 'startDate', sortOrder: SortOrder.ASC }, new Date().toISOString())
            .pipe(
                tap(({ items: newMeetings, metadata }) =>
                    ctx.setState(
                        patch<AwardCommitteeMeetingsStateModel>({
                            meetings: append<AwardCommitteeMeeting>(newMeetings),
                            metadata
                        })
                    )
                ),
                catchError(() => {
                    ctx.patchState({ loading: false });
                    return EMPTY;
                }),
                finalize(() => ctx.patchState({ loading: false }))
            );
    }
}
